import { ComponentType, useState, useCallback, useRef, useEffect } from 'react';
import { FixedSizeListProps, FixedSizeList as _FixedSizeList_ } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
} from '@artstorefronts/ui';
import { Plus, Trash2, Search, Loader2, ExternalLink } from 'lucide-react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';
import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog';

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;

const ADMIN_ARTIST_SPOTLIGHTS = gql`
  query AdminArtistSpotlights($limit: Int!, $offset: Int!) {
    adminArtistSpotlights(limit: $limit, offset: $offset) {
      items {
        id
        user {
          asfId
          firstName
          lastName
          email
          profile {
            slug
            avatarUrl
            displayName
          }
        }
        createdAt
      }
      totalCount
    }
  }
`;

const ADMIN_CREATE_ARTIST_SPOTLIGHT = gql`
  mutation AdminCreateArtistSpotlight($userId: ID!) {
    adminCreateArtistSpotlight(userId: $userId) {
      id
    }
  }
`;

const ADMIN_DELETE_ARTIST_SPOTLIGHT = gql`
  mutation AdminDeleteArtistSpotlight($id: ID!) {
    adminDeleteArtistSpotlight(id: $id)
  }
`;

const ADMIN_SEARCH_USERS = gql`
  query AdminProfiles($limit: Int, $offset: Int, $search: String) {
    adminProfiles(limit: $limit, offset: $offset, search: $search) {
      profiles {
        id
        userId
        slug
        displayName
        avatarUrl
        user {
          asfId
          firstName
          lastName
          email
        }
      }
      total
    }
  }
`;

interface User {
  asfId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  profile?: {
    slug?: string;
    avatarUrl?: string;
    displayName?: string;
  };
}

interface ArtistSpotlight {
  id: string;
  user: User;
  createdAt: string;
}

interface Profile {
  id: string;
  userId: string;
  slug?: string;
  displayName?: string;
  avatarUrl?: string;
  user: User;
}

export default function ArtistSpotlightPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ArtistSpotlight | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const PAGE_SIZE = 50;

  const { data, loading, refetch, fetchMore } = useQuery(ADMIN_ARTIST_SPOTLIGHTS, {
    variables: {
      limit: PAGE_SIZE,
      offset: 0,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: searchData, loading: searchLoading } = useQuery(ADMIN_SEARCH_USERS, {
    variables: {
      limit: 20,
      offset: 0,
      search: debouncedSearch || undefined,
    },
    skip: !addDialogOpen || !debouncedSearch,
    fetchPolicy: 'cache-and-network',
  });

  const [createSpotlight, { loading: creating }] = useMutation(ADMIN_CREATE_ARTIST_SPOTLIGHT);
  const [deleteSpotlight] = useMutation(ADMIN_DELETE_ARTIST_SPOTLIGHT);

  const spotlights = data?.adminArtistSpotlights?.items || [];
  const totalCount = data?.adminArtistSpotlights?.totalCount || 0;
  const searchResults = searchData?.adminProfiles?.profiles || [];
  const hasMoreData = spotlights.length < totalCount && totalCount > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadMoreSpotlights = useCallback(async () => {
    if (!hasMoreData || loading || loadingMore || spotlights.length >= totalCount || loadingMoreRef.current) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          offset: spotlights.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            adminArtistSpotlights: {
              ...fetchMoreResult.adminArtistSpotlights,
              items: [...prev.adminArtistSpotlights.items, ...fetchMoreResult.adminArtistSpotlights.items],
            },
          };
        },
      });
    } catch (error) {
      console.error('Error loading more spotlights:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMoreData, loading, loadingMore, spotlights.length, totalCount, fetchMore]);

  const handleOpenAddDialog = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedProfile(null);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedProfile(null);
  };

  const handleAddSpotlight = async () => {
    if (!selectedProfile) {
      toast.error(t('artist-spotlight.toast.select-artist'));
      return;
    }

    try {
      await createSpotlight({
        variables: {
          userId: selectedProfile.userId,
        },
      });

      toast.success(t('artist-spotlight.toast.added'));
      handleCloseAddDialog();
      refetch();
    } catch (error) {
      console.error('Error adding spotlight:', error);
      toast.error(error instanceof Error ? error.message : t('artist-spotlight.toast.add-failed'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSpotlight({ variables: { id: deleteTarget.id } });
      toast.success(t('artist-spotlight.toast.removed'));
      refetch();
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error removing spotlight:', error);
      toast.error(error instanceof Error ? error.message : t('artist-spotlight.toast.remove-failed'));
    } finally {
      setDeleting(false);
    }
  };

  const renderRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const spotlight = spotlights[index];

    if (hasMoreData && index >= spotlights.length - 5) {
      loadMoreSpotlights();
    }

    if (!spotlight) {
      return (
        <div style={style} className="flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      );
    }

    const displayName =
      spotlight.user.profile?.displayName ||
      [spotlight.user.firstName, spotlight.user.lastName].filter(Boolean).join(' ') ||
      t('artist-spotlight.no-name');
    const avatarUrl = spotlight.user.profile?.avatarUrl;
    const email = spotlight.user.email;
    const slug = spotlight.user.profile?.slug || '';
    const createdAt = new Date(spotlight.createdAt).toLocaleDateString();

    return (
      <div
        style={style}
        className="grid grid-cols-[300px_200px_200px_200px_1fr] gap-4 items-center px-6 border-b border-gray-100 hover:bg-gray-50"
      >
        <div className="flex items-center gap-3 min-w-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
          )}
          <span className="font-medium text-sm truncate">{displayName}</span>
        </div>
        <div className="text-sm text-gray-600 truncate">{email}</div>
        <div className="text-sm text-gray-600 truncate">{slug}</div>
        <div className="text-sm text-gray-600">{createdAt}</div>
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(spotlight)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout
      title={t('artist-spotlight.page-title')}
      description={t('artist-spotlight.page-description')}
      titleExtra={
        <Button variant="ghost" size="sm" asChild>
          <a href={`${__TOWN_SQUARE_URL__}/artist-spotlight`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-1" />
            {t('artist-spotlight.view-public')}
          </a>
        </Button>
      }
      action={
        <Button onClick={handleOpenAddDialog}>
          <Plus className="size-4" />
          {t('artist-spotlight.add-artist')}
        </Button>
      }
    >
      {loading && spotlights.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : spotlights.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-96 text-center">
          <p className="text-gray-500 mb-4">{t('artist-spotlight.empty.title')}</p>
          <Button onClick={handleOpenAddDialog}>
            <Plus className="w-4 h-4 mr-2" />
            {t('artist-spotlight.empty.cta')}
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[300px_200px_200px_200px_1fr] gap-4 px-6 py-3 border-b border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
            <div>{t('artist-spotlight.table.artist')}</div>
            <div>{t('artist-spotlight.table.email')}</div>
            <div>{t('artist-spotlight.table.slug')}</div>
            <div>{t('artist-spotlight.table.featured-date')}</div>
            <div className="text-right">{t('artist-spotlight.table.actions')}</div>
          </div>
          <div className="flex-1" style={{ height: 'calc(100vh - 280px)' }}>
            <AutoSizer>
              {({ height, width }) => (
                <List height={height} itemCount={spotlights.length} itemSize={56} width={width}>
                  {renderRow}
                </List>
              )}
            </AutoSizer>
          </div>
        </>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        isLoading={deleting}
        title={t('artist-spotlight.remove-dialog.title')}
        description={t('artist-spotlight.remove-dialog.description', {
          name: deleteTarget?.user.profile?.displayName || deleteTarget?.user.email || '',
        })}
      />

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('artist-spotlight.add-dialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="search">{t('artist-spotlight.add-dialog.search-label')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('artist-spotlight.add-dialog.search-placeholder')}
                  className="pl-9 mt-2"
                  autoFocus
                />
              </div>
            </div>

            {debouncedSearch && (
              <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                {searchLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    {t('artist-spotlight.add-dialog.no-results')}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {searchResults.map((profile: Profile) => {
                      const displayName =
                        profile.displayName ||
                        [profile.user.firstName, profile.user.lastName].filter(Boolean).join(' ') ||
                        t('artist-spotlight.no-name');
                      const isSelected = selectedProfile?.id === profile.id;

                      return (
                        <button
                          key={profile.id}
                          onClick={() => setSelectedProfile(profile)}
                          className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          {profile.avatarUrl ? (
                            <img src={profile.avatarUrl} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
                          )}
                          <div className="flex-1 text-left min-w-0">
                            <div className="font-medium text-sm truncate">{displayName}</div>
                            <div className="text-xs text-gray-500 truncate">{profile.user.email}</div>
                            {profile.slug && <div className="text-xs text-gray-400 truncate">/{profile.slug}</div>}
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAddDialog}>
              {tc('cancel')}
            </Button>
            <Button onClick={handleAddSpotlight} disabled={!selectedProfile || creating}>
              {creating && <Loader2 className="size-4 animate-spin" />}
              {t('artist-spotlight.add-artist')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
